import { z } from 'zod'
import { AxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useChangePass } from '@/services/changePassService'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { PasswordInput } from '@/components/password-input'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const formSchema = z
  .object({
    password: z.string().min(1, 'Please enter your current password'),
    newPassword: z.string().min(1, 'Please enter your new password'),
    rePassword: z.string().min(1, 'Please confirm your new password'),
  })
  .refine((data) => data.password !== data.newPassword, {
    message: 'New password must be different from old password.',
    path: ['newPassword'],
  })
  .refine((data) => data.newPassword === data.rePassword, {
    message: "Confirm password don't match.",
    path: ['rePassword'],
  })

type FormValues = z.infer<typeof formSchema>

const ChangePasswordModal = ({ open, onOpenChange }: Props) => {
  const { changePassAsync, isChangingPass } = useChangePass()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      rePassword: '',
    },
  })

  function onSubmit(data: FormValues) {
    changePassAsync({
      new_password: data.newPassword,
      old_password: data.password,
    })
      .then(() => {
        toast.success('Change Password Successfully', {
          duration: 3000,
          position: 'top-right',
        })
        form.reset()
        onOpenChange(false)
      })
      .catch((error: AxiosError) => {
        const errorMsg =
          (error.response?.data as { detail?: string })?.detail ||
          'Failed to change password'

        // Check if error is related to wrong password
        const errorMessage = errorMsg.toLowerCase()
        const isWrongPassword =
          errorMessage.includes('wrong') ||
          errorMessage.includes('incorrect') ||
          errorMessage.includes('invalid') ||
          errorMessage.includes('old password') ||
          errorMessage.includes('current password')

        if (isWrongPassword) {
          form.setError('password', {
            type: 'manual',
            message: errorMsg,
          })
          return
        }

        toast.error(errorMsg, {
          duration: 3000,
          position: 'top-right',
        })
      })
  }

  const handleClose = () => {
    form.reset()
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='gap-2 sm:max-w-[395px]'>
        <DialogHeader className='text-left'>
          <DialogTitle>Change Password</DialogTitle>
        </DialogHeader>
        <div className='mt-6'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
              <FormField
                control={form.control}
                name='password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Old Password<span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Enter old password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='newPassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      New Password<span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Enter new password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='rePassword'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Confirm Password<span className='text-red-500'>*</span>
                    </FormLabel>
                    <FormControl>
                      <PasswordInput
                        placeholder='Confirm new password'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className='mt-3 gap-2'>
                <Button
                  onClick={handleClose}
                  variant='outline'
                  type='button'
                  disabled={isChangingPass}
                >
                  Cancel
                </Button>

                <Button
                  type='submit'
                  className='min-w-[62px]'
                  disabled={isChangingPass}
                >
                  {isChangingPass ? <div className='loader'></div> : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ChangePasswordModal
